import { ExecutionPolicySchema } from "./execution-policy.js";
import { fundingRedemptionIdentityMatches } from "@repo/evm-onchain-actions/rebalance-batch-contract";
import { VaultOperationSchema } from "@repo/evm-onchain-actions/rebalance-batch-contract";
/**
 * Shared public request contracts for execution-engine callers and the Worker.
 *
 * This module intentionally contains only public wire schemas. Workflow
 * payloads, funding gates, provider registries, and venue/cash capability
 * policy remain app-local. Fields already normalized by the server continue
 * to normalize here; generic CAIP asset ids preserve their original casing
 * because that casing is part of established operation fingerprints.
 */
import {
  Parser,
  SOLANA_CHAIN_ID,
  SOLANA_CHAIN_REFERENCE,
  Validator,
  canonicalizeAssetIdLike,
  type AssetId,
} from "@repo/caip";
import {
  ExecuteActionInputSchema,
  ExecutionActionFailurePolicySchema,
  ExecutionActionScopeWireSchema,
} from "@repo/onchain-execution-pipeline/schema";
import { z } from "zod";
import { SOLANA_MAINNET_CHAIN_REF } from "./market-order-cash-assets.js";
import { SellBalanceInputSchema } from "./sell-balance.js";
import {
  BASE_WETH_USDC_ASSET_IDS,
  RestrictedRebalanceScopeSchema,
} from "./restricted-rebalance-scope.js";
import { lazySchema } from "@repo/lazy-schema";
export {
  BASE_WETH_USDC_ASSET_IDS,
  BASE_WETH_USDC_EXECUTION_SCOPE,
  RestrictedRebalanceScopeSchema,
  RESTRICTED_REBALANCE_SCOPE_HASH_DOMAIN,
  hashRestrictedRebalanceScope,
  type RestrictedRebalanceScope,
} from "./restricted-rebalance-scope.js";

export const MAX_EXECUTION_SIGNAL_BYTES = 64 * 1024;
export const MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS = 64;

export const ExecutionSignalIdSchema = lazySchema(() =>
  z.string().trim().min(1).max(128),
);

export type ExecutionSignalJsonValue =
  | null
  | boolean
  | number
  | string
  | readonly ExecutionSignalJsonValue[]
  | { readonly [key: string]: ExecutionSignalJsonValue };

const ExecutionSignalJsonValueSchema: z.ZodType<ExecutionSignalJsonValue> =
  lazySchema(() =>
    z.lazy(() =>
      z.union([
        z.null(),
        z.boolean(),
        z.number().finite(),
        z.string(),
        z.array(ExecutionSignalJsonValueSchema),
        z.record(z.string(), ExecutionSignalJsonValueSchema),
      ]),
    ),
  );

const hasOwn = (record: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

const isUnknownRecord = (
  value: unknown,
): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const addCollectionBoundIssue = (params: {
  readonly record: Readonly<Record<string, unknown>>;
  readonly key: string;
  readonly pathPrefix: readonly (string | number)[];
  readonly ctx: z.RefinementCtx;
}): void => {
  if (!hasOwn(params.record, params.key)) return;

  const value = params.record[params.key];
  if (!Array.isArray(value)) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${params.key} must be an array`,
      path: [...params.pathPrefix, params.key],
    });
    return;
  }

  if (value.length > MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      maximum: MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS,
      origin: "array",
      inclusive: true,
      message: `${params.key} must contain at most ${MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS} entries`,
      path: [...params.pathPrefix, params.key],
    });
  }
};

const addHashIssue = (params: {
  readonly value: unknown;
  readonly path: readonly (string | number)[];
  readonly ctx: z.RefinementCtx;
}): void => {
  if (
    typeof params.value !== "string" ||
    !/^0x[a-fA-F0-9]{64}$/u.test(params.value)
  ) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "must be a 32-byte 0x-prefixed hash",
      path: [...params.path],
    });
  }
};

const addHashArrayIssues = (params: {
  readonly record: Readonly<Record<string, unknown>>;
  readonly key: string;
  readonly pathPrefix: readonly (string | number)[];
  readonly ctx: z.RefinementCtx;
}): void => {
  addCollectionBoundIssue(params);
  const value = params.record[params.key];
  if (!Array.isArray(value)) return;

  const validationLength = Math.min(
    value.length,
    MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS,
  );
  for (let index = 0; index < validationLength; index += 1) {
    addHashIssue({
      value: value[index],
      path: [...params.pathPrefix, params.key, index],
      ctx: params.ctx,
    });
  }
};

const addSignalEvidenceIssues = (
  evidence: Readonly<Record<string, unknown>>,
  ctx: z.RefinementCtx,
): void => {
  const visit = (value: unknown, path: readonly (string | number)[]): void => {
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index += 1) {
        visit(value[index]!, [...path, index]);
      }
      return;
    }
    if (!isUnknownRecord(value)) return;

    if (hasOwn(value, "txHash")) {
      addHashIssue({ value: value.txHash, path: [...path, "txHash"], ctx });
    }
    for (const key of [
      "txHashes",
      "allowedUserOpHashes",
      "userOpHashes",
    ] as const) {
      if (hasOwn(value, key)) {
        addHashArrayIssues({ record: value, key, pathPrefix: path, ctx });
      }
    }
    for (const key of [
      "settlementTransactions",
      "settlementRequirements",
      "requiredMovements",
    ] as const) {
      addCollectionBoundIssue({ record: value, key, pathPrefix: path, ctx });
    }

    for (const [key, child] of Object.entries(value)) {
      visit(child, [...path, key]);
    }
  };

  visit(evidence, []);
};

const addSerializedSizeIssue = (
  value: ExecutionSignalJsonValue | Readonly<Record<string, unknown>>,
  ctx: z.RefinementCtx,
): void => {
  const serialized = JSON.stringify(value);
  const byteLength = new TextEncoder().encode(serialized).byteLength;
  if (byteLength <= MAX_EXECUTION_SIGNAL_BYTES) return;

  ctx.addIssue({
    code: z.ZodIssueCode.too_big,
    maximum: MAX_EXECUTION_SIGNAL_BYTES,
    origin: "string",
    inclusive: true,
    message: `serialized signal payload must be at most ${MAX_EXECUTION_SIGNAL_BYTES} bytes`,
  });
};

const BoundedExecutionSignalEvidenceInputSchema = lazySchema(() =>
  z.record(z.string(), z.unknown()).superRefine(addSignalEvidenceIssues),
);

export const ExecutionSignalEvidenceSchema = lazySchema(() =>
  BoundedExecutionSignalEvidenceInputSchema.pipe(
    z
      .record(z.string(), ExecutionSignalJsonValueSchema)
      .superRefine(addSerializedSizeIssue),
  ),
);

export const PositiveIntegerAmountSchema = lazySchema(() =>
  z
    .string()
    .regex(/^(?:[1-9]\d*)$/u)
    .max(80),
);

export const NonNegativeIntegerAmountSchema = lazySchema(() =>
  z
    .string()
    .regex(/^(?:0|[1-9]\d*)$/u)
    .max(80),
);

export const AssetIdSchema = lazySchema(() =>
  z.string().refine((value): boolean => Validator.isValidAssetId(value), {
    message: "Invalid assetId",
  }),
);

// Keep the public value type as `string`: callers should not need to
// manufacture CAIP brands merely to submit already runtime-validated JSON.
const ChainIdSchema = lazySchema(() =>
  z.string().refine((value): boolean => Validator.isValidChainId(value), {
    message: "Invalid chainRef",
  }),
);

/**
 * Lowercasing is part of the public boundary contract: request-key retries
 * that differ only in EVM checksum casing must produce identical inputs.
 */
export const EvmAddressSchema = lazySchema(() =>
  z
    .string()
    .regex(/^0x[a-fA-F0-9]{40}$/u)
    .transform((value) => value.toLowerCase()),
);

export const EvmTxHashSchema = lazySchema(() =>
  z.string().regex(/^0x[a-fA-F0-9]{64}$/u),
);

/**
 * Base58 Solana public key. Case is significant, so unlike `EvmAddressSchema`
 * the value is preserved exactly as submitted.
 */
export const SolanaAddressSchema = lazySchema(() =>
  z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/u),
);

/**
 * A CAIP-10 account on Solana mainnet (genesis-hash reference) with a base58
 * address. Case is significant, so unlike `EvmAccountIdSchema` the value is
 * kept exactly as submitted.
 */
export const SolanaAccountIdSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .superRefine((accountId, ctx) => {
      if (!Validator.isValidAccountId(accountId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "destination account must be a valid CAIP-10 account id",
        });
        return;
      }
      try {
        const parsed = Parser.parseAccountId(accountId);
        if (
          parsed.chain.namespace !== "solana" ||
          parsed.chain.reference !== SOLANA_CHAIN_REFERENCE ||
          !/^[1-9A-HJ-NP-Za-km-z]{32,44}$/u.test(parsed.account.address)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "destination account must identify a Solana mainnet account",
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "destination account must be a parseable CAIP-10 account id",
        });
      }
    }),
);

/**
 * The execution account a market order trades from: an EVM vault (lowercased
 * canonical form) or an Investing Account's Solana vault (its Swig wallet,
 * base58 verbatim).
 */
export const MarketOrderAccountIdSchema = lazySchema(() =>
  z.union([EvmAccountIdSchema, SolanaAccountIdSchema]),
);

const AccountIdSchema = lazySchema(() =>
  z.string().refine((value): boolean => Validator.isValidAccountId(value), {
    message: "Invalid accountRef",
  }),
);

export const EvmErc20AssetIdSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .superRefine((assetId, ctx) => {
      if (!Validator.isValidAssetId(assetId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "asset id must be a valid CAIP-19 id",
        });
        return;
      }

      try {
        const parsed = Parser.parseAssetId(assetId as AssetId);
        if (
          parsed.chain.namespace !== "eip155" ||
          parsed.asset.namespace !== "erc20" ||
          !/^0x[a-fA-F0-9]{40}$/u.test(parsed.asset.reference)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "asset id must identify an eip155 ERC-20 token",
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "asset id must be a parseable CAIP-19 id",
        });
      }
    })
    .transform((assetId) => canonicalizeAssetIdLike(assetId)),
);

/**
 * What a spec-driven (funded) buy may deliver: exactly an eip155 ERC-20 token
 * or that chain's native coin. Every EVM native coin (ETH, BNB, ...) is
 * `slip44:60` in this repo, the same identity the market-order contracts, the
 * snapshot's native `getBalance` read, and the Bebop RFQ native sentinel use.
 * Any other slip44 reference or namespace names nothing the buy pipeline can
 * hold, route, or settle, so it is refused here rather than deep in planning.
 */
export const EvmBuyAssetIdSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .superRefine((assetId, ctx) => {
      if (!Validator.isValidAssetId(assetId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "asset id must be a valid CAIP-19 id",
        });
        return;
      }

      try {
        const parsed = Parser.parseAssetId(assetId as AssetId);
        const erc20 =
          parsed.asset.namespace === "erc20" &&
          /^0x[a-fA-F0-9]{40}$/u.test(parsed.asset.reference);
        const native =
          parsed.asset.namespace === "slip44" &&
          parsed.asset.reference === "60";
        if (parsed.chain.namespace !== "eip155" || (!erc20 && !native)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "asset id must identify an eip155 ERC-20 token or the chain's native coin (slip44:60)",
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "asset id must be a parseable CAIP-19 id",
        });
      }
    })
    .transform((assetId) => canonicalizeAssetIdLike(assetId)),
);

export const EvmAccountIdSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .superRefine((accountId, ctx) => {
      if (!Validator.isValidAccountId(accountId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "destination account must be a valid CAIP-10 account id",
        });
        return;
      }

      try {
        const parsed = Parser.parseAccountId(accountId);
        const chainId = Number(parsed.chain.reference);
        if (
          parsed.chain.namespace !== "eip155" ||
          !/^[1-9]\d*$/u.test(parsed.chain.reference) ||
          !Number.isSafeInteger(chainId) ||
          !/^0x[a-fA-F0-9]{40}$/u.test(parsed.account.address)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "destination account must identify an eip155 EVM account",
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "destination account must be a parseable CAIP-10 account id",
        });
      }
    })
    .transform((accountId) => accountId.toLowerCase()),
);

export const ExecutionRequestKeySchema = lazySchema(() =>
  z.string().trim().min(1).max(512),
);
export const ExecutionCorrelationIdSchema = lazySchema(() =>
  z.string().trim().min(1).max(128),
);
export const ExecutionPortfolioIdSchema = lazySchema(() =>
  z.string().trim().min(1).max(256),
);

export const MarketOrderRfqProviderSchema = lazySchema(() =>
  z.enum([
    "bebop_rfq",
    "bebop_jam",
    "ondo_direct",
    "reality_via_bitget",
    "lifi",
    "relay",
    "uniswap_v3",
  ]),
);

export const LifiMarketOrderVenueKeySchema = lazySchema(() =>
  z.enum(["lifi", "uniswap_v3"]),
);

/**
 * The venue a Solana market order executes on: an open-market aggregator
 * swap (`solana_swap`, filled by Titan or Jupiter, chosen at execution) or
 * the Ondo Global Markets issuer path (`solana_ondo_swap`). The Ondo rule
 * decides between them from the pair's mints, so it is the venue identity a
 * preview binds; which aggregator fills is not.
 */
export const SolanaMarketOrderProviderSchema = lazySchema(() =>
  z.enum(["solana_swap", "solana_ondo_swap"]),
);

export const ExpectedMarketOrderVenueSchema = lazySchema(() =>
  z.discriminatedUnion("provider", [
    z.object({ provider: z.literal("bebop_rfq") }).strict(),
    z.object({ provider: z.literal("bebop_jam") }).strict(),
    z.object({ provider: z.literal("ondo_direct") }).strict(),
    z.object({ provider: z.literal("reality_via_bitget") }).strict(),
    z.object({ provider: z.literal("relay") }).strict(),
    z
      .object({
        provider: z.literal("lifi"),
        quoteVenueKey: LifiMarketOrderVenueKeySchema,
      })
      .strict(),
    z.object({ provider: z.literal("solana_swap") }).strict(),
    z.object({ provider: z.literal("solana_ondo_swap") }).strict(),
  ]),
);

const MarketOrderExecutionProviderSchema = lazySchema(() =>
  z.enum([
    "bebop_rfq",
    "bebop_jam",
    "ondo_direct",
    "reality_via_bitget",
    "lifi",
    "relay",
    "solana_swap",
    "solana_ondo_swap",
  ]),
);

const ExecutionCallbackEventTypeSchema = lazySchema(() =>
  z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(
      /^[A-Za-z0-9_-]+$/u,
      "callback event type may contain only letters, digits, hyphens, and underscores",
    ),
);

const LOOPBACK_CALLBACK_HOSTNAMES = new Set([
  "localhost",
  "127.0.0.1",
  "[::1]",
  "::1",
]);

const HttpsUrlSchema = lazySchema(() =>
  z
    .string()
    .url()
    .refine((value) => {
      try {
        const url = new URL(value);
        if (url.protocol === "https:") return true;
        return (
          url.protocol === "http:" &&
          LOOPBACK_CALLBACK_HOSTNAMES.has(url.hostname)
        );
      } catch {
        return false;
      }
    }, "callback url must use https (http is allowed only for loopback hosts)"),
);

export const ExecutionCallbackSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("http"),
      url: HttpsUrlSchema,
      eventType: ExecutionCallbackEventTypeSchema,
    }),
    z.object({
      kind: z.literal("automation_run"),
      runId: z.string().trim().min(1),
      eventType: ExecutionCallbackEventTypeSchema,
    }),
  ]),
);

export const ExecutionCompletionTargetSchema = lazySchema(() =>
  z.enum(["submitted", "confirmed", "settled"]),
);

export const ExecutionSvmCommitmentSchema = lazySchema(() =>
  z.enum(["processed", "confirmed", "finalized"]),
);

export const ExecutionFinalityPolicySchema = lazySchema(() =>
  z
    .object({
      evmConfirmations: z.number().int().positive().optional(),
      svmCommitment: ExecutionSvmCommitmentSchema.optional(),
    })
    .strict()
    .refine(
      (value) =>
        value.evmConfirmations !== undefined ||
        value.svmCommitment !== undefined,
      {
        message:
          "finality policy must include at least one chain finality field",
      },
    ),
);

export const ExecutionReorgPolicySchema = lazySchema(() =>
  z.discriminatedUnion("type", [
    z.object({ type: z.literal("fail") }).strict(),
    z.object({ type: z.literal("notify") }).strict(),
    z
      .object({
        type: z.literal("retry_execution"),
        maxAttempts: z.number().int().positive(),
      })
      .strict(),
    z
      .object({
        type: z.literal("call_task"),
        taskName: z.string().trim().min(1),
      })
      .strict(),
  ]),
);

export const TransferDisplaySchema = lazySchema(() =>
  z
    .object({
      amount: z.string().trim().min(1).max(64).optional(),
      assetSymbol: z.string().trim().min(1).max(32).optional(),
      fromPortfolioName: z.string().trim().min(1).max(120).optional(),
      toPortfolioName: z.string().trim().min(1).max(120).optional(),
    })
    .strict(),
);

export const WithdrawDisplaySchema = lazySchema(() =>
  z
    .object({
      amount: z.string().trim().min(1).max(64).optional(),
      assetSymbol: z.string().trim().min(1).max(32).optional(),
      sourcePortfolioName: z.string().trim().min(1).max(120).optional(),
      recipientLabel: z.string().trim().min(1).max(120).optional(),
    })
    .strict(),
);

export const DepositTransferReferenceSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z
      .object({
        kind: z.literal("evm_tx_hash"),
        value: EvmTxHashSchema,
      })
      .strict(),
    z
      .object({
        kind: z.literal("svm_signature"),
        value: z.string().trim().min(1).max(128),
      })
      .strict(),
  ]),
);

const specDrivenBuyRequestShape = lazySchema(
  () =>
    ({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      portfolioId: ExecutionPortfolioIdSchema,
      buyAssetId: EvmBuyAssetIdSchema,
      spendRaw: PositiveIntegerAmountSchema,
      destinationAccountRef: EvmAccountIdSchema.optional(),
    }) satisfies z.ZodRawShape,
);

const refineSpecDrivenBuyDestinationChain = (
  input: z.infer<z.ZodObject<typeof specDrivenBuyRequestShape>>,
  ctx: z.RefinementCtx,
): void => {
  if (input.destinationAccountRef === undefined) return;
  const destination = Parser.parseAccountId(input.destinationAccountRef);
  const buy = Parser.parseAssetId(input.buyAssetId as AssetId);
  if (destination.chain.reference !== buy.chain.reference) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "destination account must be on the buy asset chain",
      path: ["destinationAccountRef"],
    });
  }
};

export const specDrivenBuyPreviewSchema = lazySchema(() =>
  z
    .object({
      ...specDrivenBuyRequestShape,
      reviewedGrossCapRaw: PositiveIntegerAmountSchema,
    })
    .strict()
    .superRefine((input, ctx) => {
      refineSpecDrivenBuyDestinationChain(input, ctx);
      if (BigInt(input.reviewedGrossCapRaw) < BigInt(input.spendRaw)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "reviewed gross cap cannot be lower than preview spend",
          path: ["reviewedGrossCapRaw"],
        });
      }
    }),
);

const FloatRecoverySourceWireSchema = lazySchema(() =>
  z
    .object({
      recoveryChainRef: ChainIdSchema,
      recoveryAccountRef: AccountIdSchema,
      recoveryAssetId: AssetIdSchema,
      recoveryAmountRaw: PositiveIntegerAmountSchema,
      recoveryAuthority: z
        .object({
          kind: z.literal("delegated_session"),
          sessionKeyId: z.number().int().positive().safe(),
          agentId: z.number().int().positive().safe(),
        })
        .strict()
        .optional(),
    })
    .strict(),
);

export const SpecDrivenBuyFundLegWireSchema = lazySchema(() =>
  z
    .object({
      vaultOperation: VaultOperationSchema.optional(),
      sourceRedemptions: z.array(VaultOperationSchema).min(1).max(8).optional(),
      sourceChainRef: ChainIdSchema,
      sourceAccountRef: AccountIdSchema,
      sourceAssetId: AssetIdSchema,
      sourceAmountRaw: PositiveIntegerAmountSchema,
      minDestinationAmountRaw: PositiveIntegerAmountSchema,
      slippageBps: z.number().int().min(0).max(10_000).optional(),
      rail: z.enum([
        "zerodev_routing_address",
        "lifi_bridge",
        "cross_chain_swap",
        "same_chain_swap",
        "lending_redeem",
        "float_transfer",
      ]),
      settlement: z.enum(["separate_action", "settles_with_trade"]).optional(),
      floatAdvance: FloatRecoverySourceWireSchema.extend({
        // Further repayment sources beyond the primary (top-level) one.
        additionalRecoveries: z
          .array(FloatRecoverySourceWireSchema)
          .min(1)
          .max(7)
          .optional(),
      })
        .strict()
        .optional(),
      aggregatorProvider: z.enum(["lifi", "relay"]).optional(),
      railQuoteRef: z.string().min(1).optional(),
    })
    .strict()
    .superRefine((leg, ctx) => {
      if (!fundingRedemptionIdentityMatches(leg)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["vaultOperation"],
          message: "funding redemption identity mismatch",
        });
      }
      // Parent refinements also run when a child refinement has added issues.
      // Keep malformed CAIP inputs inside safeParse's ordinary failure result.
      if (
        !Validator.isValidAssetId(leg.sourceAssetId) ||
        !Validator.isValidAccountId(leg.sourceAccountRef)
      )
        return;
      const sourceAsset = Parser.parseAssetId(leg.sourceAssetId);
      const sourceAccount = Parser.parseAccountId(leg.sourceAccountRef);
      const sourceAssetChainRef = `${sourceAsset.chain.namespace}:${sourceAsset.chain.reference}`;
      const sourceAccountChainRef = `${sourceAccount.chain.namespace}:${sourceAccount.chain.reference}`;
      if (
        sourceAssetChainRef !== leg.sourceChainRef ||
        sourceAccountChainRef !== leg.sourceChainRef
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "fund leg source asset and account must be on the source chain",
          path: ["sourceChainRef"],
        });
      }
      if (
        (leg.rail === "cross_chain_swap" &&
          leg.aggregatorProvider === undefined) ||
        (leg.rail === "lifi_bridge" && leg.aggregatorProvider === "relay") ||
        (leg.rail === "float_transfer" && leg.aggregatorProvider === "lifi")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "fund leg provider must match the selected rail",
          path: ["aggregatorProvider"],
        });
      }
      if (
        (leg.rail === "float_transfer") !==
        (leg.floatAdvance !== undefined)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "floatAdvance must be present if and only if rail is float_transfer",
          path: ["floatAdvance"],
        });
      }
      if (leg.settlement !== undefined && leg.rail !== "float_transfer") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "fund leg settlement mode is only legal for float_transfer",
          path: ["settlement"],
        });
      }
    }),
);

export const SpecDrivenBuyFundPlanWireSchema = lazySchema(() =>
  z
    .object({
      kind: z.literal("fund"),
      requiredAssetId: AssetIdSchema,
      requiredAmountRaw: PositiveIntegerAmountSchema,
      legs: z.array(SpecDrivenBuyFundLegWireSchema).min(1).max(8),
      constraints: z
        .object({
          maxFundingCostBps: z.number().int().min(0).max(1_000),
          fundingTimeoutAt: z.string().datetime({ offset: true }),
          topUp: z
            .object({ maxAttempts: z.number().int().min(0).max(3) })
            .strict(),
        })
        .strict(),
    })
    .strict()
    .superRefine((plan, ctx) => {
      if (!Validator.isValidAssetId(plan.requiredAssetId)) return;
      const destinationAsset = Parser.parseAssetId(plan.requiredAssetId);
      const destinationChainRef = `${destinationAsset.chain.namespace}:${destinationAsset.chain.reference}`;
      for (const [index, leg] of plan.legs.entries()) {
        if (
          (leg.rail === "lifi_bridge" || leg.rail === "cross_chain_swap") &&
          leg.sourceChainRef === destinationChainRef
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "cross-chain fund leg must differ from the destination chain",
            path: ["legs", index, "sourceChainRef"],
          });
        }
        if (
          (leg.rail === "same_chain_swap" || leg.rail === "lending_redeem") &&
          leg.sourceChainRef !== destinationChainRef
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "same-chain fund leg must use the destination chain",
            path: ["legs", index, "sourceChainRef"],
          });
        }
      }
    }),
);

export const SpecDrivenBuyFundingExplanationWireSchema = lazySchema(() =>
  z
    .object({
      requiredAssetId: AssetIdSchema,
      requiredAssetDecimals: z.number().int().nonnegative(),
      destinationSpendableRaw: NonNegativeIntegerAmountSchema,
      nettedRaw: NonNegativeIntegerAmountSchema,
      shortfallRaw: NonNegativeIntegerAmountSchema,
      terminalDeliverableRaw: NonNegativeIntegerAmountSchema,
      legs: z.array(
        z
          .object({
            source: z
              .object({
                chainRef: ChainIdSchema,
                accountRef: AccountIdSchema,
                assetId: AssetIdSchema,
                symbol: z.string().min(1),
                decimals: z.number().int().nonnegative(),
                amountRaw: PositiveIntegerAmountSchema,
              })
              .strict(),
            terminal: z
              .object({
                assetId: AssetIdSchema,
                decimals: z.number().int().nonnegative(),
                minAmountRaw: PositiveIntegerAmountSchema,
              })
              .strict(),
            rail: z.enum([
              "zerodev_routing_address",
              "lifi_bridge",
              "cross_chain_swap",
              "same_chain_swap",
              "lending_redeem",
              "float_transfer",
            ]),
            slippageBps: z.number().int().min(0).max(10_000).optional(),
            aggregatorProvider: z.enum(["lifi", "relay"]).optional(),
            railQuoteRef: z.string().min(1).optional(),
            expectedCostBps: z.number().int().nonnegative(),
            etaSeconds: z.number().int().nonnegative().optional(),
            paddingBps: z.number().int().nonnegative(),
            sourceValueInRequiredRaw: NonNegativeIntegerAmountSchema.optional(),
            expectedCostScore: NonNegativeIntegerAmountSchema,
          })
          .strict(),
      ),
      candidatePlanCount: z.number().int().nonnegative(),
      searchTruncation: z
        .object({
          maxSourcesPerAssetChain: z.number().int().positive(),
          omittedSourceCount: z.number().int().nonnegative(),
          groups: z.array(
            z
              .object({
                chainRef: ChainIdSchema,
                assetId: AssetIdSchema,
                sourceCount: z.number().int().nonnegative(),
                consideredSourceCount: z.number().int().nonnegative(),
              })
              .strict(),
          ),
          quoteSearch: z
            .object({
              reason: z.literal("quote_replan_budget_exhausted"),
              maxReplanIterations: z.number().int().positive(),
              untriedCandidateCount: z.number().int().positive(),
            })
            .strict()
            .optional(),
        })
        .strict()
        .optional(),
      // Present only on an unfunded result: sources whose provider quote
      // refused to fund the request, and why.
      railRejections: z
        .array(
          z
            .object({
              rail: z.enum([
                "zerodev_routing_address",
                "lifi_bridge",
                "cross_chain_swap",
                "same_chain_swap",
                "lending_redeem",
                "float_transfer",
              ]),
              aggregatorProvider: z.enum(["lifi", "relay"]).optional(),
              sourceChainRef: ChainIdSchema,
              sourceAssetId: AssetIdSchema,
              // A string, not an enum: the engine may add reasons before every
              // deployed client knows them.
              reason: z.string().min(1),
            })
            .strict(),
        )
        .optional(),
      decidedBy: z.discriminatedUnion("criterion", [
        z.object({ criterion: z.literal("only_viable_plan") }).strict(),
        z.object({ criterion: z.literal("no_viable_plan") }).strict(),
        z
          .object({
            criterion: z.literal("max_terminal_deliverable"),
            winnerTerminalRaw: NonNegativeIntegerAmountSchema,
            runnerUpTerminalRaw: NonNegativeIntegerAmountSchema,
          })
          .strict(),
        z
          .object({
            criterion: z.literal("fewest_legs"),
            winnerLegCount: z.number().int().nonnegative(),
            runnerUpLegCount: z.number().int().nonnegative(),
          })
          .strict(),
        z
          .object({
            criterion: z.literal("lowest_expected_cost"),
            commonDecimals: z.number().int().nonnegative(),
            winnerExpectedCostScore: NonNegativeIntegerAmountSchema,
            runnerUpExpectedCostScore: NonNegativeIntegerAmountSchema,
          })
          .strict(),
        z
          .object({
            criterion: z.literal("priority_order"),
            winnerPriorityOrder: z.array(z.number().int().nonnegative()),
            runnerUpPriorityOrder: z.array(z.number().int().nonnegative()),
          })
          .strict(),
        z
          .object({
            criterion: z.literal("deterministic_tie_break"),
            winnerKey: z.string(),
            runnerUpKey: z.string(),
          })
          .strict(),
      ]),
      humanExplanation: z.string().min(1),
    })
    .strict(),
);

const SpecDrivenBuyPlanBasisIntegritySchema = lazySchema(() =>
  z.string().regex(/^sbb1\.[a-f0-9]{64}$/u),
);

export const UnsignedSpecDrivenBuyPlanBasisSchema = lazySchema(() =>
  z
    .object({
      buyingPowerRaw: NonNegativeIntegerAmountSchema,
      quotedSpendRaw: PositiveIntegerAmountSchema,
      expiresAt: z.number().int().nonnegative(),
      plan: SpecDrivenBuyFundPlanWireSchema,
      explanation: SpecDrivenBuyFundingExplanationWireSchema,
      waveCount: z.number().int().positive(),
    })
    .strict(),
);

export const SpecDrivenBuyPlanBasisSchema = lazySchema(() =>
  UnsignedSpecDrivenBuyPlanBasisSchema.extend({
    integrity: SpecDrivenBuyPlanBasisIntegritySchema,
  }).strict(),
);

export const specDrivenBuyCreateSchema = lazySchema(() =>
  z
    .object({
      ...specDrivenBuyRequestShape,
      expectedVenue: ExpectedMarketOrderVenueSchema.optional(),
      planBasis: SpecDrivenBuyPlanBasisSchema,
    })
    .strict()
    .superRefine(refineSpecDrivenBuyDestinationChain),
);

// Client-facing names retained as aliases so adopting the shared package does
// not require a public API rename; the Worker uses the shorter server names.
export const executionSpecDrivenBuyPreviewSchema = specDrivenBuyPreviewSchema;
export const executionSpecDrivenBuyCreateSchema = specDrivenBuyCreateSchema;

export const RawExecutionActionInputSchema = lazySchema(() =>
  z.object({
    type: z.string().min(1, "action type is required"),
    scope: ExecutionActionScopeWireSchema,
    args: z.unknown(),
    failurePolicy: ExecutionActionFailurePolicySchema.optional(),
  }),
);

type MarketOrderChainIdentityInput = {
  readonly chainRef?: string;
  readonly chainId?: number;
};

type ParsedMarketOrderChainRef =
  | { readonly success: true; readonly chainId: number }
  | {
      readonly success: false;
      readonly reason: "invalid" | "invalid_eip155" | "unsupported_namespace";
    };

/**
 * The CAIP-2 reference of a market-order chain id: eip155 chains by their
 * decimal id, Solana mainnet by its genesis hash. Every market-order chain
 * identity is normalized through this one rule.
 */
export const marketOrderChainRefForChainId = (chainId: number): string =>
  chainId === SOLANA_CHAIN_ID ? SOLANA_MAINNET_CHAIN_REF : `eip155:${chainId}`;

/**
 * Market orders trade on eip155 chains and on Solana mainnet, which the engine
 * keys by the numeric network id `@repo/caip` reserves for it. Any other
 * Solana reference (a cluster name, devnet) is unsupported.
 */
const parseMarketOrderChainRef = (
  chainRef: string,
): ParsedMarketOrderChainRef => {
  let parsed: ReturnType<typeof Parser.parseChainId>;
  try {
    parsed = Parser.parseChainId(chainRef);
  } catch {
    return { success: false, reason: "invalid" };
  }

  if (
    parsed.chain.namespace === "solana" &&
    parsed.chain.reference === SOLANA_CHAIN_REFERENCE
  ) {
    return { success: true, chainId: SOLANA_CHAIN_ID };
  }
  if (parsed.chain.namespace !== "eip155") {
    return { success: false, reason: "unsupported_namespace" };
  }
  if (!/^\d+$/u.test(parsed.chain.reference)) {
    return { success: false, reason: "invalid_eip155" };
  }

  const chainId = Number(parsed.chain.reference);
  if (!Number.isSafeInteger(chainId) || chainId <= 0) {
    return { success: false, reason: "invalid_eip155" };
  }

  return { success: true, chainId };
};

const addMarketOrderChainRefIssue = (
  chainRef: string,
  ctx: z.RefinementCtx,
): number | null => {
  const parsed = parseMarketOrderChainRef(chainRef);
  if (parsed.success) return parsed.chainId;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message:
      parsed.reason === "unsupported_namespace"
        ? "market orders support eip155 chains and Solana mainnet only"
        : parsed.reason === "invalid_eip155"
          ? "eip155 chainRef must contain a safe positive decimal chain id"
          : "chainRef must be a valid CAIP-2 chain reference",
    path: ["chainRef"],
  });
  return null;
};

const refineRawMarketOrderChainIdentity = (
  input: MarketOrderChainIdentityInput,
  ctx: z.RefinementCtx,
): void => {
  const hasChainRef = input.chainRef !== undefined;
  const hasChainId = input.chainId !== undefined;
  if (hasChainRef === hasChainId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "exactly one of chainRef or chainId is required",
      path: [hasChainRef ? "chainRef" : "chainId"],
    });
    return;
  }

  if (input.chainRef !== undefined) {
    addMarketOrderChainRefIssue(input.chainRef, ctx);
  }
};

const refineNormalizedMarketOrderChainIdentity = (
  input: Required<MarketOrderChainIdentityInput>,
  ctx: z.RefinementCtx,
): void => {
  const derivedChainId = addMarketOrderChainRefIssue(input.chainRef, ctx);
  if (derivedChainId !== null && input.chainId !== derivedChainId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "chainId must match the chainRef",
      path: ["chainId"],
    });
  }
};

function normalizeMarketOrderChainIdentity<
  TInput extends MarketOrderChainIdentityInput,
>(
  input: TInput,
): TInput & { readonly chainRef: string; readonly chainId: number };
function normalizeMarketOrderChainIdentity(
  input: MarketOrderChainIdentityInput,
) {
  let chainId = input.chainId;
  if (chainId === undefined) {
    if (input.chainRef === undefined) {
      throw new Error("market-order chain identity invariant violated");
    }
    const parsed = parseMarketOrderChainRef(input.chainRef);
    if (!parsed.success) {
      throw new Error("market-order chainRef refinement invariant violated");
    }
    chainId = parsed.chainId;
  }

  return {
    ...input,
    chainRef: marketOrderChainRefForChainId(chainId),
    chainId,
  };
}

const readEvmTradableAsset = (
  assetId: string,
): { readonly chainId: number; readonly kind: "erc20" | "native" } | null => {
  try {
    const parsed = Parser.parseAssetId(assetId as AssetId);
    if (parsed.chain.namespace !== "eip155") return null;
    const chainId = Number.parseInt(parsed.chain.reference, 10);
    if (!Number.isSafeInteger(chainId) || chainId <= 0) return null;
    if (parsed.asset.namespace === "erc20") {
      return { chainId, kind: "erc20" };
    }
    if (
      parsed.asset.namespace === "slip44" &&
      parsed.asset.reference === "60"
    ) {
      return { chainId, kind: "native" };
    }
    return null;
  } catch {
    return null;
  }
};

const MarketOrderIntentShape = lazySchema(
  () =>
    ({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      portfolioId: ExecutionPortfolioIdSchema,
      sellAssetId: AssetIdSchema,
      buyAssetId: AssetIdSchema,
      sellAmount: PositiveIntegerAmountSchema,
      provider: MarketOrderRfqProviderSchema.optional(),
      to: EvmAddressSchema.optional(),
      toPortfolioId: ExecutionPortfolioIdSchema.optional(),
      expiryType: z.enum(["standard", "short"]).optional(),
      feeBps: z.number().int().min(0).max(500).optional(),
    }) satisfies z.ZodRawShape,
);

const RawMarketOrderIntentSchema = lazySchema(() =>
  z.object({
    ...MarketOrderIntentShape,
    chainId: z.number().int().positive().optional(),
    chainRef: z.string().min(1).optional(),
  }),
);

const NormalizedMarketOrderIntentSchema = lazySchema(() =>
  z.object({
    ...MarketOrderIntentShape,
    chainId: z.number().int().positive(),
    chainRef: z.string().min(1),
  }),
);

const MarketOrderCreateExtensionShape = lazySchema(
  () =>
    ({
      quoteVenueKey: LifiMarketOrderVenueKeySchema.optional(),
      expectedVenue: ExpectedMarketOrderVenueSchema.optional(),
      completionTarget: ExecutionCompletionTargetSchema.optional(),
      callback: ExecutionCallbackSchema.optional(),
    }) satisfies z.ZodRawShape,
);

export type ExecutionMarketOrderVenueRefinementInput = {
  readonly buyAssetId: string;
  readonly chainId: number;
  readonly chainRef: string;
  readonly provider?: z.infer<typeof MarketOrderRfqProviderSchema>;
  readonly sellAssetId: string;
};

/**
 * App-local policy hook for provider capabilities and chain cash registries.
 * Inputs have validated CAIP syntax and normalized chain identity; the custom
 * refiner owns EVM venue admission when supplied.
 */
export type ExecutionMarketOrderVenueRefinement = (
  input: ExecutionMarketOrderVenueRefinementInput,
  ctx: z.RefinementCtx,
) => void;

export type ExecutionMarketOrderSchemaFactoryOptions = {
  readonly refineVenueCapabilities?: ExecutionMarketOrderVenueRefinement;
};

const refinePureMarketOrderIntent = (
  input: ExecutionMarketOrderVenueRefinementInput,
  ctx: z.RefinementCtx,
): void => {
  for (const side of ["sellAssetId", "buyAssetId"] as const) {
    const asset = readEvmTradableAsset(input[side]);
    if (!asset || asset.chainId !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${side} must be an EVM token asset on chainId`,
        path: [side],
      });
    }
  }
};

/**
 * Builds market-order schemas with an optional app-owned provider refinement.
 * Consumers that do not own venue knowledge can use the exported default
 * schemas, which still enforce every pure wire invariant.
 */
export const createExecutionMarketOrderRequestSchemas = (
  options: ExecutionMarketOrderSchemaFactoryOptions = {},
) => {
  const refineIntent = (
    input: z.infer<typeof NormalizedMarketOrderIntentSchema>,
    ctx: z.RefinementCtx,
  ): void => {
    if (input.to && input.toPortfolioId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "only one destination may be provided",
        path: ["to"],
      });
    }
    if (options.refineVenueCapabilities) {
      options.refineVenueCapabilities(input, ctx);
    } else {
      refinePureMarketOrderIntent(input, ctx);
    }
  };

  const executionMarketOrderCreateSchema = z
    .union([
      RawMarketOrderIntentSchema.extend({ ...MarketOrderCreateExtensionShape })
        .superRefine(refineRawMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
      NormalizedMarketOrderIntentSchema.extend({
        ...MarketOrderCreateExtensionShape,
      })
        .superRefine(refineNormalizedMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
    ])
    .superRefine(refineIntent);

  const executionMarketOrderQuotePreviewSchema = z
    .union([
      RawMarketOrderIntentSchema.strict()
        .superRefine(refineRawMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
      NormalizedMarketOrderIntentSchema.strict()
        .superRefine(refineNormalizedMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
    ])
    .superRefine(refineIntent);

  return {
    executionMarketOrderCreateSchema,
    executionMarketOrderQuotePreviewSchema,
  };
};

// Built with the first default market-order schema touched, not at load.
const defaultMarketOrderRequestSchemas = lazySchema(() =>
  createExecutionMarketOrderRequestSchemas(),
);

/**
 * Admission-lax defaults for clients without venue policy. The Worker layers
 * refineEvmMarketOrderRoute through createExecutionMarketOrderRequestSchemas.
 */
export const executionMarketOrderCreateSchema = lazySchema(
  () => defaultMarketOrderRequestSchemas.executionMarketOrderCreateSchema,
);
export const executionMarketOrderQuotePreviewSchema = lazySchema(
  () => defaultMarketOrderRequestSchemas.executionMarketOrderQuotePreviewSchema,
);

export const MAX_EXECUTION_OPERATION_ACTIONS = 50;

export const liquidationCreateSchema = lazySchema(() =>
  z
    .object({
      portfolioId: ExecutionPortfolioIdSchema,
      chainId: z.number().int().positive(),
      assets: z
        .array(EvmErc20AssetIdSchema)
        .min(1)
        .max(MAX_EXECUTION_OPERATION_ACTIONS),
      settlementAssetId: EvmErc20AssetIdSchema,
      slippageBps: z.number().int().min(0).max(10_000).default(100),
      marketHoursPolicy: z.enum(["hold", "reject"]).default("hold"),
      partialDelivery: z.enum(["deliver", "withhold"]).default("deliver"),
      recipient: EvmAddressSchema.optional(),
      idempotencyKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
    })
    .strict()
    .superRefine((input, ctx) => {
      const seenAssets = new Set<string>();
      for (const [index, assetId] of input.assets.entries()) {
        const parsed = Parser.parseAssetId(assetId as AssetId);
        if (parsed.chain.reference !== String(input.chainId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "asset chain must match chainId",
            path: ["assets", index],
          });
        }
        if (seenAssets.has(assetId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "assets must not contain duplicates",
            path: ["assets", index],
          });
        }
        seenAssets.add(assetId);
      }

      const settlementAsset = Parser.parseAssetId(
        input.settlementAssetId as AssetId,
      );
      if (settlementAsset.chain.reference !== String(input.chainId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "settlementAssetId chain must match chainId",
          path: ["settlementAssetId"],
        });
      }
      if (seenAssets.has(input.settlementAssetId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "settlementAssetId must not appear in assets",
          path: ["settlementAssetId"],
        });
      }
    }),
);

const MarketOrderReferenceShape = lazySchema(
  () =>
    ({
      flow: z.literal("market_order"),
      provider: MarketOrderExecutionProviderSchema,
      intentSide: z.enum(["buy", "sell", "swap"]),
      sellAssetId: AssetIdSchema,
      buyAssetId: AssetIdSchema,
      sellAmount: PositiveIntegerAmountSchema,
      cashAssetId: AssetIdSchema.nullable(),
      quoteAssetId: AssetIdSchema.nullable(),
    }) satisfies z.ZodRawShape,
);

const RawMarketOrderReferenceSchema = lazySchema(() =>
  z
    .object({
      ...MarketOrderReferenceShape,
      chainId: z.number().int().positive().optional(),
      chainRef: z.string().min(1).optional(),
    })
    .strict()
    .superRefine(refineRawMarketOrderChainIdentity)
    .transform(normalizeMarketOrderChainIdentity),
);

const NormalizedMarketOrderReferenceSchema = lazySchema(() =>
  z
    .object({
      ...MarketOrderReferenceShape,
      chainId: z.number().int().positive(),
      chainRef: z.string().min(1),
    })
    .strict()
    .superRefine(refineNormalizedMarketOrderChainIdentity)
    .transform(normalizeMarketOrderChainIdentity),
);

export const MarketOrderReferenceWireSchema = lazySchema(() =>
  z.union([
    RawMarketOrderReferenceSchema,
    NormalizedMarketOrderReferenceSchema,
  ]),
);

export const ExecutionOperationCreateShape = lazySchema(
  () =>
    ({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      initiatorOwnerAddress: z.string().trim().min(1).optional(),
      actions: z
        .array(RawExecutionActionInputSchema)
        .min(1)
        .max(MAX_EXECUTION_OPERATION_ACTIONS),
      completionTarget: ExecutionCompletionTargetSchema.optional(),
      finality: ExecutionFinalityPolicySchema.optional(),
      operationKind: z
        .enum(["execution", "withdraw", "transfer", "bridge"])
        .optional(),
      onReorg: ExecutionReorgPolicySchema.optional(),
      callback: ExecutionCallbackSchema.optional(),
      requiresInternalExecution: z.boolean().optional(),
      transferDisplay: TransferDisplaySchema.optional(),
      withdrawDisplay: WithdrawDisplaySchema.optional(),
      marketOrder: MarketOrderReferenceWireSchema.optional(),
    }) satisfies z.ZodRawShape,
);

export const executionOperationCreateSchema = lazySchema(() =>
  z
    .object({ ...ExecutionOperationCreateShape })
    .strict()
    .superRefine((input, ctx) => {
      for (const [actionIndex, action] of input.actions.entries()) {
        if (
          action.type === "owner_withdrawal_batch" ||
          action.type === "external_withdrawal_batch"
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Withdrawal batches require their dedicated withdrawal route",
            path: ["actions", actionIndex, "type"],
          });
        }
        if (
          typeof action.args !== "object" ||
          action.args === null ||
          Array.isArray(action.args)
        ) {
          continue;
        }
        for (const key of Object.keys(action.args)) {
          if (
            key !== "bridgeIntentPurpose" &&
            key !== "executionAccountRef" &&
            !/^funding.*AccountRef$/u.test(key)
          ) {
            continue;
          }
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${key} is reserved for internal compiler payloads`,
            path: ["actions", actionIndex, "args", key],
          });
        }
      }
    }),
);

const readEvmAssetChainId = (assetId: string): number | null => {
  try {
    const parsed = Parser.parseAssetId(assetId as AssetId);
    if (parsed.chain.namespace !== "eip155") return null;
    const chainId = Number.parseInt(parsed.chain.reference, 10);
    return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
  } catch {
    return null;
  }
};

/** One VM a portfolio transfer or withdrawal moves assets on. */
type MoneyMovementChainNamespace = {
  /** The request's numeric chain id for a chain reference, or null. */
  readonly chainId: (reference: string) => number | null;
  readonly movesAsset: (asset: {
    readonly namespace: string;
    readonly reference: string;
  }) => boolean;
  /**
   * The canonical spelling of an account address a withdrawal may pay on
   * this VM, or null when the value is not one.
   */
  readonly canonicalAccountAddress: (address: string) => string | null;
};

/**
 * The VMs a portfolio transfer or withdrawal moves assets on, by CAIP-2
 * namespace: the chain id, which assets of that chain move, and what an
 * account address looks like there. A new VM is one entry.
 */
const TRANSFER_CHAIN_NAMESPACES: ReadonlyMap<
  string,
  MoneyMovementChainNamespace
> = new Map<string, MoneyMovementChainNamespace>([
  [
    "eip155",
    {
      chainId: (reference) => {
        const chainId = Number.parseInt(reference, 10);
        return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
      },
      movesAsset: () => true,
      canonicalAccountAddress: (address) =>
        /^0x[a-fA-F0-9]{40}$/u.test(address) ? address.toLowerCase() : null,
    },
  ],
  [
    "solana",
    {
      // Mainnet only, keyed by the numeric id `@repo/caip` reserves for it.
      chainId: (reference) =>
        reference === SOLANA_CHAIN_REFERENCE ? SOLANA_CHAIN_ID : null,
      movesAsset: (asset) =>
        (asset.namespace === "spl" &&
          SolanaAddressSchema.safeParse(asset.reference).success) ||
        (asset.namespace === "slip44" && asset.reference === "501"),
      // Base58 is case-significant: kept exactly as given.
      canonicalAccountAddress: (address) =>
        SolanaAddressSchema.safeParse(address).success ? address : null,
    },
  ],
]);

/** The VM and chain id a transfer or withdrawal moves this asset on, or null. */
const readTransferAssetChain = (
  assetId: string,
): {
  readonly chainId: number;
  readonly namespace: MoneyMovementChainNamespace;
} | null => {
  try {
    const parsed = Parser.parseAssetId(assetId as AssetId);
    const namespace = TRANSFER_CHAIN_NAMESPACES.get(parsed.chain.namespace);
    if (!namespace?.movesAsset(parsed.asset)) return null;
    const chainId = namespace.chainId(parsed.chain.reference);
    return chainId === null ? null : { chainId, namespace };
  } catch {
    return null;
  }
};

/** The chain id a portfolio transfer moves this asset on, or null. */
const readTransferAssetChainId = (assetId: string): number | null =>
  readTransferAssetChain(assetId)?.chainId ?? null;

const readEvmErc20Asset = (
  assetId: string,
): { readonly address: string; readonly chainId: number } | null => {
  try {
    const parsed = Parser.parseAssetId(assetId as AssetId);
    if (
      parsed.chain.namespace !== "eip155" ||
      parsed.asset.namespace !== "erc20" ||
      !/^0x[a-fA-F0-9]{40}$/u.test(parsed.asset.reference)
    ) {
      return null;
    }
    const chainId = Number.parseInt(parsed.chain.reference, 10);
    if (!Number.isSafeInteger(chainId) || chainId <= 0) return null;
    return { address: parsed.asset.reference.toLowerCase(), chainId };
  } catch {
    return null;
  }
};

const OndoDirectBatchSellLegSchema = lazySchema(() =>
  z
    .object({
      assetId: AssetIdSchema,
      assetTokenAddress: EvmAddressSchema.optional(),
      amount: PositiveIntegerAmountSchema,
      minCashOutRaw: PositiveIntegerAmountSchema.optional(),
    })
    .strict(),
);

const OndoDirectBatchBuyLegSchema = lazySchema(() =>
  z
    .object({
      assetId: AssetIdSchema,
      assetTokenAddress: EvmAddressSchema.optional(),
      cashAmount: PositiveIntegerAmountSchema,
      minTokenOutRaw: PositiveIntegerAmountSchema.optional(),
    })
    .strict(),
);

const validateOndoDirectBatchLegAsset = (params: {
  readonly assetId: string;
  readonly assetTokenAddress?: string;
  readonly cashAssetId: string;
  readonly chainId: number;
  readonly ctx: z.RefinementCtx;
  readonly path: readonly (string | number)[];
}): void => {
  const asset = readEvmErc20Asset(params.assetId);
  if (!asset || asset.chainId !== params.chainId) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "assetId must be an ERC-20 asset on chainId",
      path: [...params.path, "assetId"],
    });
    return;
  }

  if (params.assetId.toLowerCase() === params.cashAssetId.toLowerCase()) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "leg assetId must be different from cashAssetId",
      path: [...params.path, "assetId"],
    });
  }

  if (params.assetTokenAddress && params.assetTokenAddress !== asset.address) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "assetTokenAddress must match assetId",
      path: [...params.path, "assetTokenAddress"],
    });
  }
};

export const executionOndoDirectBatchRebalanceCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      portfolioId: ExecutionPortfolioIdSchema,
      chainId: z.number().int().positive(),
      cashAssetId: AssetIdSchema,
      cashTokenAddress: EvmAddressSchema,
      cashInputAmount: PositiveIntegerAmountSchema.optional(),
      sells: z.array(OndoDirectBatchSellLegSchema).max(50).optional(),
      buys: z.array(OndoDirectBatchBuyLegSchema).max(50).optional(),
      completionTarget: ExecutionCompletionTargetSchema.optional(),
      finality: ExecutionFinalityPolicySchema.optional(),
      onReorg: ExecutionReorgPolicySchema.optional(),
      callback: ExecutionCallbackSchema.optional(),
      residualCashPolicy: z.literal("sweep_to_user").optional(),
      approvalMode: z
        .enum(["require_existing", "submit_missing_approvals"])
        .optional(),
    })
    .strict()
    .superRefine((input, ctx) => {
      if ((input.sells?.length ?? 0) + (input.buys?.length ?? 0) === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "at least one Ondo Direct batch leg is required",
          path: ["buys"],
        });
      }

      const cashAsset = readEvmErc20Asset(input.cashAssetId);
      if (!cashAsset || cashAsset.chainId !== input.chainId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "cashAssetId must be an ERC-20 asset on chainId",
          path: ["cashAssetId"],
        });
      } else if (input.cashTokenAddress !== cashAsset.address) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "cashTokenAddress must match cashAssetId",
          path: ["cashTokenAddress"],
        });
      }

      const legs = [
        ...(input.sells ?? []).map((leg, index) => ({
          leg,
          side: "sells" as const,
          index,
        })),
        ...(input.buys ?? []).map((leg, index) => ({
          leg,
          side: "buys" as const,
          index,
        })),
      ];

      for (const { leg, side, index } of legs) {
        const path = [side, index] as const;
        validateOndoDirectBatchLegAsset({
          assetId: leg.assetId,
          assetTokenAddress: leg.assetTokenAddress,
          cashAssetId: input.cashAssetId,
          chainId: input.chainId,
          ctx,
          path,
        });
      }
    }),
);

export const OndoDirectRebalanceTargetSchema = lazySchema(() =>
  z
    .object({
      assetId: AssetIdSchema,
      weightBps: z.number().int().min(0).max(10_000),
    })
    .strict(),
);

const refineOndoDirectRebalancePlanInput = (
  input: {
    readonly chainId: number;
    readonly cashAssetId: string;
    readonly cashTokenAddress: string;
    readonly targets: readonly { assetId: string; weightBps: number }[];
    readonly positions?: readonly string[];
  },
  ctx: z.RefinementCtx,
): void => {
  const cashAsset = readEvmErc20Asset(input.cashAssetId);
  if (!cashAsset || cashAsset.chainId !== input.chainId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "cashAssetId must be an ERC-20 asset on chainId",
      path: ["cashAssetId"],
    });
  } else if (input.cashTokenAddress !== cashAsset.address) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "cashTokenAddress must match cashAssetId",
      path: ["cashTokenAddress"],
    });
  }

  const seenTargets = new Set<string>();
  let weightSum = 0;
  for (const [index, target] of input.targets.entries()) {
    weightSum += target.weightBps;
    const targetKey = target.assetId.toLowerCase();
    if (seenTargets.has(targetKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "duplicate target assetId",
        path: ["targets", index, "assetId"],
      });
    }
    seenTargets.add(targetKey);

    if (targetKey === input.cashAssetId.toLowerCase()) continue;
    const asset = readEvmErc20Asset(target.assetId);
    if (!asset || asset.chainId !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "target assetId must be an ERC-20 asset on chainId",
        path: ["targets", index, "assetId"],
      });
    }
  }
  if (weightSum !== 10_000) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "target weights must sum to exactly 10000 basis points",
      path: ["targets"],
    });
  }

  for (const [index, assetId] of (input.positions ?? []).entries()) {
    if (assetId.toLowerCase() === input.cashAssetId.toLowerCase()) continue;
    const asset = readEvmErc20Asset(assetId);
    if (!asset || asset.chainId !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "position assetId must be an ERC-20 asset on chainId",
        path: ["positions", index],
      });
    }
  }
};

export const executionOndoDirectRebalancePlanPreviewSchema = lazySchema(() =>
  z
    .object({
      portfolioId: ExecutionPortfolioIdSchema,
      chainId: z.number().int().positive(),
      cashAssetId: AssetIdSchema,
      cashTokenAddress: EvmAddressSchema,
      targets: z.array(OndoDirectRebalanceTargetSchema).min(1).max(50),
      positions: z.array(AssetIdSchema).max(100).optional(),
    })
    .strict()
    .superRefine(refineOndoDirectRebalancePlanInput),
);

export const executionOndoDirectRebalancePlanCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      portfolioId: ExecutionPortfolioIdSchema,
      chainId: z.number().int().positive(),
      cashAssetId: AssetIdSchema,
      cashTokenAddress: EvmAddressSchema,
      targets: z.array(OndoDirectRebalanceTargetSchema).min(1).max(50),
      positions: z.array(AssetIdSchema).max(100).optional(),
      completionTarget: ExecutionCompletionTargetSchema.optional(),
      finality: ExecutionFinalityPolicySchema.optional(),
      onReorg: ExecutionReorgPolicySchema.optional(),
      callback: ExecutionCallbackSchema.optional(),
      residualCashPolicy: z.literal("sweep_to_user").optional(),
      approvalMode: z
        .enum(["require_existing", "submit_missing_approvals"])
        .optional(),
    })
    .strict()
    .superRefine(refineOndoDirectRebalancePlanInput),
);

/**
 * The onramp fee repayment the owner confirmed on the preview, in USD. The
 * engine never charges more: a withdrawal that would repay more is refused
 * (`withdrawal_onramp_fee_changed`) so the client previews again. Omitted is
 * a confirmed $0.
 */
export const ConfirmedOnrampFeeUsdSchema = lazySchema(() =>
  z
    .string()
    .regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/u, "A USD amount in cents"),
);

/** What an owner withdrawal selects: exact amounts on canonical EVM accounts. */
const ownerWithdrawalSelectionFields = lazySchema(() => ({
  portfolioId: ExecutionPortfolioIdSchema,
  expectedOwnerAddress: EvmAddressSchema,
  assets: z
    .array(
      z
        .object({
          accountRef: z.string(),
          assetId: z.string(),
          amountRaw: PositiveIntegerAmountSchema.refine(
            (value) => /^[1-9]\d*$/u.test(value) && BigInt(value) < 1n << 256n,
            { message: "Amount must fit an EVM uint256" },
          ),
        })
        .strict(),
    )
    .min(1)
    .max(MAX_EXECUTION_OPERATION_ACTIONS),
}));

/** Owner-only in-kind withdrawal. Financial facts and the recipient are server-owned. */
export const executionOwnerWithdrawalCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      ...ownerWithdrawalSelectionFields,
      confirmedOnrampFeeUsd: ConfirmedOnrampFeeUsdSchema.optional(),
    })
    .strict()
    .superRefine(refineOwnerWithdrawalSelection),
);

/** What an owner withdrawal would repay, before it is requested. */
export const executionOwnerWithdrawalPreviewInputSchema = lazySchema(() =>
  z
    .object({ ...ownerWithdrawalSelectionFields })
    .strict()
    .superRefine(refineOwnerWithdrawalSelection),
);

/**
 * The onramp fee repayment an owner withdrawal carries, or null: part of one
 * selected asset (or, delivered as USDC, of the USDC), paid to Glider's fee
 * wallet, worth `usd`. Echoes the selection it priced. Unknown keys are
 * tolerated so the engine can add fields without breaking an open tab.
 */
export const executionOwnerWithdrawalPreviewSchema = lazySchema(() =>
  z.object({
    portfolioId: ExecutionPortfolioIdSchema,
    assets: ownerWithdrawalSelectionFields.assets,
    onrampFee: z
      .object({
        assetId: AssetIdSchema,
        amountRaw: PositiveIntegerAmountSchema,
        usd: z
          .string()
          .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/u)
          .max(80),
      })
      .nullable(),
  }),
);

function refineOwnerWithdrawalSelection(
  input: {
    readonly portfolioId: string;
    readonly assets: readonly {
      readonly accountRef: string;
      readonly assetId: string;
    }[];
  },
  ctx: z.RefinementCtx,
): void {
  let previous: string | undefined;
  input.assets.forEach((asset, index) => {
    if (
      !SellBalanceInputSchema.safeParse({
        portfolioId: input.portfolioId,
        accountRef: asset.accountRef,
        assetId: asset.assetId,
      }).success
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["assets", index],
        message: "Account and asset must identify the same canonical EVM chain",
      });
    }
    const key = `${asset.accountRef}/${asset.assetId}`;
    if (previous !== undefined && previous >= key) {
      ctx.addIssue({
        code: "custom",
        path: ["assets", index],
        message:
          "Assets must be sorted by accountRef and assetId, without duplicates",
      });
    }
    previous = key;
  });
}

/**
 * What a withdrawal moves, wherever it lives: an asset a transfer moves on
 * `chainId`, paid to an account address of that chain's VM. The recipient
 * comes back in its VM's canonical spelling (EVM lowercased, base58 exact).
 */
const withdrawalIntentFields = lazySchema(() => ({
  portfolioId: z.string().trim().min(1),
  chainId: z.number().int().positive(),
  assetId: AssetIdSchema,
  amount: PositiveIntegerAmountSchema,
  recipient: z.string().trim().min(1),
}));

const canonicalizeWithdrawalIntent = <
  Input extends {
    readonly chainId: number;
    readonly assetId: string;
    readonly recipient: string;
  },
>(
  input: Input,
  ctx: z.RefinementCtx,
): Input => {
  const chain = readTransferAssetChain(input.assetId);
  if (chain?.chainId !== input.chainId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "assetId must be an asset a withdrawal moves on chainId",
      path: ["assetId"],
    });
    return z.NEVER;
  }
  const recipient = chain.namespace.canonicalAccountAddress(input.recipient);
  if (recipient === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "recipient must be an account address on chainId",
      path: ["recipient"],
    });
    return z.NEVER;
  }
  return { ...input, recipient };
};

export const executionWithdrawalCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: z.string().trim().min(1),
      correlationId: z.string().trim().min(1).optional(),
      ...withdrawalIntentFields,
      completionTarget: ExecutionCompletionTargetSchema.default("settled"),
      display: WithdrawDisplaySchema.optional(),
      callback: ExecutionCallbackSchema.optional(),
      confirmedOnrampFeeUsd: ConfirmedOnrampFeeUsdSchema.optional(),
    })
    .transform(canonicalizeWithdrawalIntent),
);

/** The economics of a withdrawal before it is requested. */
export const executionWithdrawalPreviewInputSchema = lazySchema(() =>
  z
    .object({ ...withdrawalIntentFields })
    .strict()
    .transform(canonicalizeWithdrawalIntent),
);

/**
 * One cost of a withdrawal, one entry per kind. `platform` is Glider's
 * withdrawal fee: paid by the owner out of the withdrawn amount, in the
 * withdrawn asset (`assetId`, base units `amount`, worth `usd`).
 * `onramp_fee` repays onramp fees Glider covered for the owner's deposits,
 * paid the same way as `platform` and listed only when the withdrawal repays
 * some. `network` is the chain's transaction fees and any account the
 * recipient needs opened; listed only where the engine knows who pays them,
 * with null amounts.
 */
export const ExecutionWithdrawalFeeSchema = lazySchema(() =>
  z.object({
    kind: z.enum(["platform", "onramp_fee", "network"]),
    paidBy: z.enum(["owner", "glider"]),
    assetId: AssetIdSchema.nullable(),
    amount: PositiveIntegerAmountSchema.nullable(),
    usd: z
      .string()
      .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/u)
      .max(80)
      .nullable(),
  }),
);

/**
 * What a withdrawal would do: `amount` leaves the vault, `recipientAmount`
 * reaches the recipient, and every fee the owner pays in the withdrawn asset
 * makes up the difference. Unknown keys are tolerated so the engine can add
 * fields without breaking an open tab.
 */
export const executionWithdrawalPreviewSchema = lazySchema(() =>
  z
    .object({
      ...withdrawalIntentFields,
      recipientAmount: PositiveIntegerAmountSchema,
      fees: z.array(ExecutionWithdrawalFeeSchema).max(8),
    })
    .superRefine((preview, ctx) => {
      const ownerPaid = preview.fees.reduce(
        (sum, fee) =>
          fee.paidBy === "owner" &&
          fee.assetId === preview.assetId &&
          fee.amount !== null
            ? sum + BigInt(fee.amount)
            : sum,
        0n,
      );
      if (
        BigInt(preview.recipientAmount) + ownerPaid !==
        BigInt(preview.amount)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "amount must equal recipientAmount plus the fees it pays",
          path: ["recipientAmount"],
        });
      }
      const kinds = new Set(preview.fees.map((fee) => fee.kind));
      if (kinds.size !== preview.fees.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "one fee entry per kind",
          path: ["fees"],
        });
      }
    }),
);

const TransferAssetSchema = lazySchema(() =>
  z.object({
    assetId: AssetIdSchema,
    amount: PositiveIntegerAmountSchema,
  }),
);

export const executionTransferCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: z.string().trim().min(1),
      correlationId: z.string().trim().min(1).optional(),
      fromPortfolioId: z.string().trim().min(1),
      toPortfolioId: z.string().trim().min(1),
      chainId: z.number().int().positive(),
      assetId: AssetIdSchema.optional(),
      amount: PositiveIntegerAmountSchema.optional(),
      assets: z.array(TransferAssetSchema).min(1).max(20).optional(),
      completionTarget: ExecutionCompletionTargetSchema.default("settled"),
      display: TransferDisplaySchema.optional(),
      callback: ExecutionCallbackSchema.optional(),
    })
    .superRefine((input, ctx) => {
      if (input.fromPortfolioId === input.toPortfolioId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "transfer destination must be a different portfolio",
          path: ["toPortfolioId"],
        });
      }

      if (input.assets && (input.assetId || input.amount)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "provide either assets or assetId/amount",
          path: ["assets"],
        });
      }

      if (!input.assets && (!input.assetId || !input.amount)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "transfer asset and amount are required",
          path: ["assetId"],
        });
        return;
      }

      const transferAssets = input.assets ?? [
        { assetId: input.assetId as AssetId, amount: input.amount as string },
      ];
      for (const [index, asset] of transferAssets.entries()) {
        if (readTransferAssetChainId(asset.assetId) === input.chainId) continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "assetId must be an asset a transfer moves on chainId",
          path: input.assets ? ["assets", index, "assetId"] : ["assetId"],
        });
      }
    }),
);

export const executionBridgeCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: z.string().trim().min(1),
      correlationId: z.string().trim().min(1).optional(),
      portfolioId: z.string().trim().min(1),
      provider: z.enum(["lifi", "relay"]).optional(),
      sourceChainId: z.number().int().positive(),
      destinationChainId: z.number().int().positive(),
      assetId: AssetIdSchema,
      amount: PositiveIntegerAmountSchema,
      destinationAssetId: AssetIdSchema,
      slippageBps: z.number().int().min(0).max(10_000).optional(),
      completionTarget: ExecutionCompletionTargetSchema.default("settled"),
      display: TransferDisplaySchema.optional(),
      callback: ExecutionCallbackSchema.optional(),
    })
    .superRefine((input, ctx) => {
      if (input.sourceChainId === input.destinationChainId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "sourceChainId and destinationChainId must differ",
          path: ["destinationChainId"],
        });
      }

      if (readEvmAssetChainId(input.assetId) !== input.sourceChainId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "assetId must be an EVM asset on sourceChainId",
          path: ["assetId"],
        });
      }

      if (
        readEvmAssetChainId(input.destinationAssetId) !==
        input.destinationChainId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "destinationAssetId must be an EVM asset on destinationChainId",
          path: ["destinationAssetId"],
        });
      }
    }),
);

export const executionDepositIntentCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: z.string().trim().min(1),
      correlationId: z.string().trim().min(1).optional(),
      portfolioId: z.string().trim().min(1),
      chainId: z.number().int().positive(),
      assetId: AssetIdSchema.optional(),
      amount: PositiveIntegerAmountSchema.optional(),
      vaultAddress: EvmAddressSchema.optional(),
    })
    .superRefine((input, ctx) => {
      if (
        input.assetId &&
        readEvmAssetChainId(input.assetId) !== input.chainId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "assetId must be an EVM asset on chainId",
          path: ["assetId"],
        });
      }
    }),
);

export const executionDepositTransactionAttachSchema = lazySchema(() =>
  z
    .object({
      txHash: EvmTxHashSchema.optional(),
      reference: DepositTransferReferenceSchema.optional(),
    })
    .superRefine((input, ctx) => {
      if (!input.txHash && !input.reference) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "deposit transfer reference is required",
          path: ["reference"],
        });
        return;
      }

      if (
        input.txHash &&
        input.reference &&
        input.reference.kind !== "evm_tx_hash"
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "txHash can only be paired with an evm transfer reference",
          path: ["reference"],
        });
      }

      if (
        input.txHash &&
        input.reference?.kind === "evm_tx_hash" &&
        input.reference.value.toLowerCase() !== input.txHash.toLowerCase()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "txHash and evm transfer reference must match",
          path: ["reference"],
        });
      }
    }),
);

const ExternalEventBaseShape = lazySchema(
  () =>
    ({
      signalId: ExecutionSignalIdSchema,
      actionIndex: z.number().int().nonnegative(),
      detail: z.string().trim().min(1).optional(),
      data: ExecutionSignalEvidenceSchema.optional(),
    }) as const,
);

const executionOperationEventWireSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z
      .object({
        ...ExternalEventBaseShape,
        kind: z.literal("quote_ready"),
        quote: ExecutionSignalEvidenceSchema.optional(),
      })
      .strict(),
    z
      .object({
        ...ExternalEventBaseShape,
        kind: z.literal("action_progress"),
        phase: z.string().trim().min(1).max(100),
      })
      .strict(),
    z
      .object({
        ...ExternalEventBaseShape,
        kind: z.literal("action_submitted"),
        submission: ExecutionSignalEvidenceSchema.optional(),
      })
      .strict(),
    z
      .object({
        ...ExternalEventBaseShape,
        kind: z.literal("action_confirmed"),
        receipt: ExecutionSignalEvidenceSchema.optional(),
      })
      .strict(),
    z
      .object({
        ...ExternalEventBaseShape,
        kind: z.literal("action_settled"),
        settlement: ExecutionSignalEvidenceSchema.optional(),
      })
      .strict(),
    z
      .object({
        ...ExternalEventBaseShape,
        kind: z.literal("action_succeeded"),
        receipt: ExecutionSignalEvidenceSchema.optional(),
      })
      .strict(),
    z
      .object({
        ...ExternalEventBaseShape,
        kind: z.literal("action_failed"),
        error: z.string().trim().min(1).optional(),
      })
      .strict(),
  ]),
);

export const executionOperationEventSchema = lazySchema(() =>
  executionOperationEventWireSchema.superRefine((input, ctx) => {
    addSerializedSizeIssue(input, ctx);
  }),
);

const executionOperationSettlementAckWireSchema = lazySchema(() =>
  z.discriminatedUnion("kind", [
    z
      .object({
        signalId: ExecutionSignalIdSchema,
        kind: z.literal("indexing"),
        actionIndex: z.number().int().nonnegative().optional(),
        detail: z.string().trim().min(1).optional(),
        data: ExecutionSignalEvidenceSchema.optional(),
      })
      .strict(),
    z
      .object({
        signalId: ExecutionSignalIdSchema,
        kind: z.literal("settled"),
        actionIndex: z.number().int().nonnegative().optional(),
        detail: z.string().trim().min(1).optional(),
        settlement: ExecutionSignalEvidenceSchema.optional(),
      })
      .strict(),
  ]),
);

export const executionOperationSettlementAckSchema = lazySchema(() =>
  executionOperationSettlementAckWireSchema.superRefine((input, ctx) => {
    addSerializedSizeIssue(input, ctx);
  }),
);

export const MAX_REBALANCE_HOLDINGS = 256;

export const RebalanceTargetEntrySchema = lazySchema(() =>
  z
    .object({
      baseAssetId: AssetIdSchema,
      totalUsdAllocation: z.number().nonnegative(),
      totalUsdTargetedForYield: z.number().nonnegative().default(0),
      desiredYieldPositions: z
        .array(
          z
            .object({
              yieldSourceId: z.string().trim().min(1),
              usdValueTargeted: z.number().nonnegative(),
              vaultAddress: EvmAddressSchema.optional(),
            })
            .strict(),
        )
        .default([]),
    })
    .strict(),
);

export const RebalanceTargetAllocationSchema = lazySchema(() =>
  z
    .record(z.string(), RebalanceTargetEntrySchema)
    .superRefine((allocation, ctx) => {
      if (Object.keys(allocation).length > MAX_REBALANCE_HOLDINGS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Target allocation must contain at most ${MAX_REBALANCE_HOLDINGS} assets`,
        });
      }
    }),
);

export const RebalanceHoldingSchema = lazySchema(() =>
  z
    .object({
      assetId: AssetIdSchema,
      chainId: z.number().int().positive(),
      amountRaw: NonNegativeIntegerAmountSchema,
      decimals: z.number().int().min(0).max(36),
      priceUsd: z.string().trim().min(1),
      unit: z.literal("per_token"),
      asOf: z.string().datetime({ offset: true }).optional(),
      sourceRef: z.string().optional(),
    })
    .strict(),
);

export const RebalanceIssuerMetadataSchema = lazySchema(() =>
  z
    .record(
      z.string(),
      z.object({
        issuerId: z.string().trim().min(1),
        batchSpender: EvmAddressSchema.optional(),
      }),
    )
    .optional(),
);

/**
 * Compatibility vault addresses are ignored by admission, so the value schema
 * only needs to admit every VM the engine can address: lowercased EVM hex or
 * a base58 Solana public key.
 */
const RebalanceVaultAddressSchema = lazySchema(() =>
  z.union([EvmAddressSchema, SolanaAddressSchema]),
);

type RebalanceChainIdentityInput = {
  readonly chainRef?: string;
  readonly chainId?: number;
};

const parseRebalanceChainRef = (
  chainRef: string,
): ReturnType<typeof Parser.parseChainId> | null => {
  try {
    return Parser.parseChainId(chainRef);
  } catch {
    return null;
  }
};

const readEip155ChainIdFromParsedRef = (
  parsed: ReturnType<typeof Parser.parseChainId>,
): number | null => {
  if (
    parsed.chain.namespace !== "eip155" ||
    !/^\d+$/u.test(parsed.chain.reference)
  ) {
    return null;
  }
  const chainId = Number(parsed.chain.reference);
  return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
};

/**
 * `chainRef` is the additive, namespace-aware alternative to the numeric
 * `chainId`. An eip155 chainRef must agree with an explicit chainId; any other
 * namespace has no numeric chain id, so supplying one is a contradiction.
 */
const refineRebalanceChainIdentity = (
  input: RebalanceChainIdentityInput,
  ctx: z.RefinementCtx,
): void => {
  if (input.chainRef === undefined) return;
  const parsed = parseRebalanceChainRef(input.chainRef);
  if (parsed === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "chainRef must be a valid CAIP-2 chain reference",
      path: ["chainRef"],
    });
    return;
  }
  if (parsed.chain.namespace === "eip155") {
    const chainId = readEip155ChainIdFromParsedRef(parsed);
    if (chainId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "eip155 chainRef must contain a safe positive decimal chain id",
        path: ["chainRef"],
      });
      return;
    }
    if (input.chainId !== undefined && input.chainId !== chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "chainId must match the eip155 chainRef",
        path: ["chainId"],
      });
    }
    return;
  }
  if (input.chainId !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "chainId applies to eip155 chains only; omit it when chainRef names another namespace",
      path: ["chainId"],
    });
  }
};

/**
 * Fills the numeric `chainId` from an eip155 `chainRef` so existing numeric
 * consumers keep working unchanged. A bare `chainId` is left as submitted and a
 * non-eip155 `chainRef` never acquires a numeric id.
 */
function normalizeRebalanceChainIdentity<
  TInput extends RebalanceChainIdentityInput,
>(input: TInput): TInput {
  if (input.chainRef === undefined || input.chainId !== undefined) {
    return input;
  }
  const parsed = parseRebalanceChainRef(input.chainRef);
  if (parsed === null) return input;
  const chainId = readEip155ChainIdFromParsedRef(parsed);
  return chainId === null ? input : { ...input, chainId };
}

export const rebalanceCreateSchema = lazySchema(() =>
  z
    .object({
      requestKey: ExecutionRequestKeySchema,
      correlationId: ExecutionCorrelationIdSchema.optional(),
      initiatorOwnerAddress: z.string().trim().min(1).optional(),
      portfolioId: ExecutionPortfolioIdSchema,
      chainId: z.number().int().positive().safe().optional(),
      chainRef: ChainIdSchema.optional().describe(
        "CAIP-2 chain reference; the namespace-aware alternative to `chainId`",
      ),
      completionTarget: ExecutionCompletionTargetSchema.optional(),
      to: RebalanceTargetAllocationSchema.optional(),
      strategy: z.record(z.string(), z.unknown()).optional(),
      executionScope: RestrictedRebalanceScopeSchema.optional(),
      executionPolicy: ExecutionPolicySchema.optional(),
      currentHoldings: z
        .array(RebalanceHoldingSchema)
        .max(MAX_REBALANCE_HOLDINGS)
        .describe(
          "Deprecated for create: amounts, decimals, and prices are ignored; preview may use asset ids as a selection hint",
        ),
      vaultAddresses: z
        .record(z.string(), RebalanceVaultAddressSchema)
        .describe("Accepted for compatibility and fully ignored")
        .optional(),
      issuerMetadata: RebalanceIssuerMetadataSchema.describe(
        "Accepted for compatibility and fully ignored",
      ),
      adapterPayload: z
        .record(z.string(), z.record(z.string(), z.unknown()))
        .describe("Accepted for compatibility and fully ignored")
        .optional(),
      inputFingerprint: z
        .string()
        .trim()
        .min(1)
        .max(512)
        .describe("Accepted for compatibility and fully ignored")
        .optional(),
      callback: ExecutionCallbackSchema.optional(),
    })
    .strict()
    .superRefine((data, ctx) => {
      refineRebalanceChainIdentity(data, ctx);
      if (data.executionPolicy && !data.initiatorOwnerAddress) {
        ctx.addIssue({
          code: "custom",
          message: "Execution policy requires an asserted owner",
          path: ["initiatorOwnerAddress"],
        });
      }
      if (data.executionPolicy && data.executionScope) {
        ctx.addIssue({
          code: "custom",
          message: "Execution policy cannot override a restricted scope",
          path: ["executionPolicy"],
        });
      }
      if (data.executionScope) {
        const allowed: readonly string[] = BASE_WETH_USDC_ASSET_IDS;
        if (
          data.chainId !== 8453 ||
          data.to === undefined ||
          data.strategy !== undefined ||
          Object.keys(data.to).length === 0 ||
          Object.entries(data.to).some(
            ([assetId, target]) =>
              !allowed.includes(assetId) ||
              target.baseAssetId !== assetId ||
              target.desiredYieldPositions.length !== 0 ||
              target.totalUsdTargetedForYield !== 0,
          )
        ) {
          ctx.addIssue({
            code: "custom",
            message:
              "Restricted scope requires Base WETH/USDC token targets only",
            path: ["executionScope"],
          });
        }
      }
      const selectedAssetIds = new Set(
        [
          ...Object.keys(data.to ?? {}),
          ...data.currentHoldings.map((holding) => holding.assetId),
        ].map((assetId) => canonicalizeAssetIdLike(assetId)),
      );
      if (selectedAssetIds.size > MAX_REBALANCE_HOLDINGS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Rebalance selection must contain at most ${MAX_REBALANCE_HOLDINGS} unique assets`,
          path: ["currentHoldings"],
        });
      }
      const hasTo = data.to !== undefined;
      const hasStrategy = data.strategy !== undefined;
      if (!hasTo && !hasStrategy) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Exactly one of `to` or `strategy` must be supplied",
          path: ["to"],
        });
      }
      if (hasTo && hasStrategy) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "`to` and `strategy` are mutually exclusive",
          path: ["strategy"],
        });
      }
      if (
        data.currentHoldings.length === 0 &&
        data.chainId === undefined &&
        data.chainRef === undefined
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Empty compatibility holdings require an explicit `chainId` or `chainRef`",
          path: ["currentHoldings"],
        });
      }
    })
    .transform(normalizeRebalanceChainIdentity),
);

export type ExecutionCallback = z.infer<typeof ExecutionCallbackSchema>;
export type ExecutionCompletionTarget = z.infer<
  typeof ExecutionCompletionTargetSchema
>;
export type ExecutionSvmCommitment = z.infer<
  typeof ExecutionSvmCommitmentSchema
>;
export type ExecutionFinalityPolicy = z.infer<
  typeof ExecutionFinalityPolicySchema
>;
export type ExecutionReorgPolicy = z.infer<typeof ExecutionReorgPolicySchema>;
export type TransferDisplay = z.infer<typeof TransferDisplaySchema>;
export type WithdrawDisplay = z.infer<typeof WithdrawDisplaySchema>;
export type RawExecutionActionInput = z.input<
  typeof RawExecutionActionInputSchema
>;
export type ValidatedExecutionActionInput = z.infer<
  typeof ExecuteActionInputSchema
>;
export type SpecDrivenBuyPreviewInput = z.infer<
  typeof specDrivenBuyPreviewSchema
>;
export type UnsignedSpecDrivenBuyPlanBasis = z.infer<
  typeof UnsignedSpecDrivenBuyPlanBasisSchema
>;
export type SpecDrivenBuyPlanBasis = z.infer<
  typeof SpecDrivenBuyPlanBasisSchema
>;
export type SpecDrivenBuyCreateInput = z.infer<
  typeof specDrivenBuyCreateSchema
>;
export type LiquidationCreateRequest = z.input<typeof liquidationCreateSchema>;
export type LiquidationCreateInput = z.output<typeof liquidationCreateSchema>;
export type ExecutionOperationCreateInput = z.input<
  typeof executionOperationCreateSchema
>;
export type ExecutionMarketOrderCreateInput = z.input<
  typeof executionMarketOrderCreateSchema
>;
export type NormalizedExecutionMarketOrderCreateInput = z.output<
  typeof executionMarketOrderCreateSchema
>;
export type ExecutionMarketOrderQuotePreviewInput = z.input<
  typeof executionMarketOrderQuotePreviewSchema
>;
export type NormalizedExecutionMarketOrderQuotePreviewInput = z.output<
  typeof executionMarketOrderQuotePreviewSchema
>;
export type ExecutionOndoDirectBatchRebalanceCreateInput = z.infer<
  typeof executionOndoDirectBatchRebalanceCreateSchema
>;
export type ExecutionOndoDirectRebalancePlanPreviewInput = z.infer<
  typeof executionOndoDirectRebalancePlanPreviewSchema
>;
export type ExecutionOndoDirectRebalancePlanCreateInput = z.infer<
  typeof executionOndoDirectRebalancePlanCreateSchema
>;
export type ExecutionOwnerWithdrawalCreateInput = z.infer<
  typeof executionOwnerWithdrawalCreateSchema
>;
export type ExecutionWithdrawalCreateInput = z.infer<
  typeof executionWithdrawalCreateSchema
>;
export type ExecutionWithdrawalPreviewInput = z.infer<
  typeof executionWithdrawalPreviewInputSchema
>;
export type ExecutionOwnerWithdrawalPreviewInput = z.infer<
  typeof executionOwnerWithdrawalPreviewInputSchema
>;
export type ExecutionOwnerWithdrawalPreview = z.infer<
  typeof executionOwnerWithdrawalPreviewSchema
>;
export type ExecutionWithdrawalFee = z.infer<
  typeof ExecutionWithdrawalFeeSchema
>;
export type ExecutionWithdrawalPreview = z.infer<
  typeof executionWithdrawalPreviewSchema
>;
export type ExecutionTransferCreateInput = z.infer<
  typeof executionTransferCreateSchema
>;
export type ExecutionBridgeCreateInput = z.infer<
  typeof executionBridgeCreateSchema
>;
export type ExecutionDepositIntentCreateInput = z.infer<
  typeof executionDepositIntentCreateSchema
>;
export type ExecutionDepositTransactionAttachInput = z.infer<
  typeof executionDepositTransactionAttachSchema
>;
export type DepositTransferReference = z.infer<
  typeof DepositTransferReferenceSchema
>;
export type ExecutionOperationEventInput = z.infer<
  typeof executionOperationEventSchema
>;
export type ExecutionOperationSettlementAckInput = z.infer<
  typeof executionOperationSettlementAckSchema
>;
export type ExecutionOperationEventKind = ExecutionOperationEventInput["kind"];
export type RebalanceCreateInput = z.infer<typeof rebalanceCreateSchema>;
